import React, { useRef, useLayoutEffect, useState } from 'react';
import { CarWindowDto } from './GlassControl.types';
import { FixedElementBadge } from '../shared/FixedElementBadge';

interface WindowLabelMobileProps {
  data: CarWindowDto;
  label: string;
  position: { top: string; left: string };
  isActive?: boolean;
  showPointer?: boolean;
  pointerDirection?: 'top' | 'bottom' | 'left' | 'right' | 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  pointerHeight?: number;
  isHighlighted?: boolean;
  onClick?: () => void;
}

type GrowDirection = 'left' | 'right' | 'center';
type PointerDirection = 'top' | 'bottom' | 'left' | 'right' | 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';

/**
 * Określa kierunek rozszerzania elementu na podstawie pointerDirection
 */
function getGrowDirectionFromPointer(pointerDirection: PointerDirection): GrowDirection {
  if (pointerDirection.endsWith('-left')) return 'right';
  if (pointerDirection.endsWith('-right')) return 'left';
  return 'center';
}

/**
 * Oblicza przesunięcie X na podstawie kierunku rozszerzania i szerokości elementu
 */
function calculateTranslateX(growDirection: GrowDirection, width: number): number {
  switch (growDirection) {
    case 'right':
      return 0;
    case 'left':
      return -width;
    case 'center':
    default:
      return -width / 2;
  }
}

export function WindowLabelMobile({
  data,
  label,
  position,
  isActive = false,
  showPointer = false,
  pointerDirection = 'bottom',
  pointerHeight = 20,
  isHighlighted = false,
  onClick,
}: WindowLabelMobileProps) {
  const { manufacturer, dateCode, absence } = data;
  const containerRef = useRef<HTMLDivElement>(null);
  const [translateX, setTranslateX] = useState(0);

  const growDirection = getGrowDirectionFromPointer(pointerDirection);

  useLayoutEffect(() => {
    if (containerRef.current) {
      const width = containerRef.current.offsetWidth;
      setTranslateX(calculateTranslateX(growDirection, width));
    }
  }, [growDirection, manufacturer, dateCode, label]);

  const renderPointer = () => {
    if (!showPointer) return null;

    const offset = pointerHeight;
    const circleOffset = pointerHeight + 6;

    const getPointerConfig = (): {
      lineStyle: React.CSSProperties;
      circleStyle: React.CSSProperties;
    } => {
      switch (pointerDirection) {
        case 'top':
          return {
            lineStyle: {
              top: `-${offset}px`,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '2px',
              height: `${pointerHeight}px`,
            },
            circleStyle: {
              top: `-${circleOffset}px`,
              left: '50%',
              transform: 'translateX(-50%)',
            },
          };
        case 'bottom':
          return {
            lineStyle: {
              bottom: `-${offset}px`,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '2px',
              height: `${pointerHeight}px`,
            },
            circleStyle: {
              bottom: `-${circleOffset}px`,
              left: '50%',
              transform: 'translateX(-50%)',
            },
          };
        case 'left':
          return {
            lineStyle: {
              left: `-${offset}px`,
              top: '50%',
              transform: 'translateY(-50%)',
              width: `${pointerHeight}px`,
              height: '2px',
            },
            circleStyle: {
              left: `-${circleOffset}px`,
              top: '50%',
              transform: 'translateY(-50%)',
            },
          };
        case 'right':
          return {
            lineStyle: {
              right: `-${offset}px`,
              top: '50%',
              transform: 'translateY(-50%)',
              width: `${pointerHeight}px`,
              height: '2px',
            },
            circleStyle: {
              right: `-${circleOffset}px`,
              top: '50%',
              transform: 'translateY(-50%)',
            },
          };
        case 'bottom-left':
          return {
            lineStyle: {
              bottom: `-${offset}px`,
              left: '25px',
              transform: 'translateX(-50%)',
              width: '2px',
              height: `${pointerHeight}px`,
            },
            circleStyle: {
              bottom: `-${circleOffset}px`,
              left: '25px',
              transform: 'translateX(-50%)',
            },
          };
        case 'bottom-right':
          return {
            lineStyle: {
              bottom: `-${offset}px`,
              right: '25px',
              transform: 'translateX(50%)',
              width: '2px',
              height: `${pointerHeight}px`,
            },
            circleStyle: {
              bottom: `-${circleOffset}px`,
              right: '25px',
              transform: 'translateX(50%)',
            },
          };
        case 'top-left':
          return {
            lineStyle: {
              top: `-${offset}px`,
              left: '25px',
              transform: 'translateX(-50%)',
              width: '2px',
              height: `${pointerHeight}px`,
            },
            circleStyle: {
              top: `-${circleOffset}px`,
              left: '25px',
              transform: 'translateX(-50%)',
            },
          };
        case 'top-right':
          return {
            lineStyle: {
              top: `-${offset}px`,
              right: '25px',
              transform: 'translateX(50%)',
              width: '2px',
              height: `${pointerHeight}px`,
            },
            circleStyle: {
              top: `-${circleOffset}px`,
              right: '25px',
              transform: 'translateX(50%)',
            },
          };
        default:
          return {
            lineStyle: {},
            circleStyle: {},
          };
      }
    };

    const { lineStyle, circleStyle } = getPointerConfig();

    return (
      <>
        <div
          className="absolute bg-navy-900"
          style={lineStyle}
        />
        <div
          className="absolute w-1.5 h-1.5 rounded-full bg-navy-900"
          style={circleStyle}
        />
      </>
    );
  };

  const highlightStyle = isHighlighted
    ? 'ring-2 ring-[#0064FF] bg-navy-700'
    : '';

  if (absence) {
    return (
      <div
        ref={containerRef}
        className="absolute cursor-pointer"
        style={{
          left: position.left,
          top: position.top,
          transform: `translateX(${translateX}px)`,
        }}
        onClick={onClick}
      >
        <div className={`flex items-center rounded-[10px] px-3 py-2 transition-all duration-200 ${isHighlighted ? highlightStyle : 'bg-navy-900'}`}>
          <div className="flex flex-col gap-1 leading-none whitespace-nowrap">
            <span className="text-[10px] font-medium text-blue-300 tracking-[0.2px] font-archivo">
              {label}
            </span>
            <span className="text-[12px] font-semibold text-white tracking-[0.3px]">
              Brak
            </span>
          </div>
        </div>
        {renderPointer()}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="absolute cursor-pointer group"
      style={{
        left: position.left,
        top: position.top,
        transform: `translateX(${translateX}px)`,
      }}
      onClick={onClick}
    >
      <div
        className={`flex items-center rounded-[10px] transition-all duration-200 ${
          isHighlighted
            ? highlightStyle
            : isActive ? 'bg-navy-700' : 'bg-navy-900'
        }`}
      >
        {/* Data produkcji - tylko jeśli jest dateCode */}
        {dateCode && (
          <>
            <div className="flex justify-center items-center px-2 py-2">
              <span className="text-[10px] font-medium text-blue-300 leading-[12px] text-center whitespace-nowrap font-archivo">
                {dateCode}
              </span>
            </div>

            {/* Separator */}
            <div
              className={`h-[40px] w-px ${isActive ? 'bg-navy-600' : 'bg-navy-800'}`}
            />
          </>
        )}

        {/* Informacje o szybie */}
        <div className="flex flex-col gap-1 px-3 py-2 leading-none whitespace-nowrap">
          <span className="text-[10px] font-medium text-blue-300 tracking-[0.2px] font-archivo">
            {label}
          </span>
          <span className="text-[12px] font-semibold text-white tracking-[0.3px]">
            {manufacturer || 'Brak danych'}
          </span>
        </div>
      </div>

      {/* Ikona naprawy - jeśli szyba była wymieniana */}
      {data.isReplaced && (
        <div className="absolute -top-4 -left-2 z-20">
          <FixedElementBadge disclaimerText="Element wymieniany" isOpen={isHighlighted} />
        </div>
      )}

      {renderPointer()}
    </div>
  );
}
